/**
 * PricelistInputs / PricelistDetails — pricelist «Входные параметры» +
 * «Расчёт» bodies (TASK-192). Moved out of ServiceManagementPage.tsx
 * (KAN-157 god-component split).
 */
import type { Service } from '@/services/api/services';
import { pricingItems } from '@/utils/serviceFormula';
import { CalcEmptyNotice } from './CalcEmptyNotice';

// «Входные параметры» content for a pricelist (TASK-192): a per-position
// «количество '<name|work>' (<unit>)» line. Lives in the unified inputs section
// (data-testid="pricelist-inputs"). Rendered ONLY when there are positions.
// NOTE: PricelistInputs carries the «Входные параметры» heading INSIDE its own
// pricelist-inputs div (TASK-192 asserts within(pricelist-inputs).getByText(
// /Входные параметры/i)). So when this fills the inputs section, the section's
// DetailSection MUST NOT add a second «Входные параметры» <h4> — the inputs
// section is rendered specially for the pricelist method (see ServiceDetailPanel).
export function PricelistInputs({ service }: { service: Service }): JSX.Element {
  const items = pricingItems(service);
  return (
    <div data-testid="pricelist-inputs">
      <h4 className="text-sm font-semibold text-gray-700 mb-2">Входные параметры</h4>
      <ul className="text-sm text-gray-700 space-y-0.5">
        {items.map((item, idx) => {
          const name = item.name ?? item.work ?? '—';
          return (
            <li key={idx}>
              количество '{name}'{item.unit ? ` (${item.unit})` : ''}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// «Расчёт» body for a pricelist: the positions table + the multiplication
// formula (data-testid="pricelist-formula", TASK-192). The «Входные параметры»
// block was moved to PricelistInputs (TASK-197). An empty pricelist keeps the
// «Нет позиций прайс-листа» message and renders no formula.
export function PricelistDetails({ service }: { service: Service }): JSX.Element {
  const items = pricingItems(service);

  if (items.length === 0) {
    return <CalcEmptyNotice testid="pricelist-empty" message="Нет позиций прайс-листа" />;
  }

  // Show the variant column only if at least one item carries a variant.
  const hasVariant = items.some(it => it && it.variant);

  // Pricelist formula: per-term enumeration for small lists, Σ for large ones.
  const formula =
    items.length <= 8
      ? `итого = ${items
          .map(item => {
            const name = item.name ?? item.work ?? '—';
            const price = item.price !== undefined && item.price !== null ? String(item.price) : 'цена';
            return `кол_'${name}' × ${price}`;
          })
          .join(' + ')}`
      : 'итого = Σ (количество × цена)';

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left text-gray-500 border-b">
              <th className="py-2 pr-4 font-medium">Наименование</th>
              {hasVariant && <th className="py-2 pr-4 font-medium">Вариант</th>}
              <th className="py-2 pr-4 font-medium">Цена</th>
              <th className="py-2 font-medium">Ед.</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => {
              const label = item.name ?? item.work ?? '—';
              return (
                <tr key={idx} className="border-b last:border-b-0">
                  <td className="py-2 pr-4 text-gray-800">{label}</td>
                  {hasVariant && <td className="py-2 pr-4 text-gray-600">{item.variant ?? ''}</td>}
                  <td className="py-2 pr-4 text-gray-800">
                    {item.price !== undefined && item.price !== null ? String(item.price) : '—'}
                  </td>
                  <td className="py-2 text-gray-600">{item.unit ?? ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Формула */}
      <div data-testid="pricelist-formula">
        <div className="text-xs font-medium text-gray-500 mb-1">Формула</div>
        <div className="rounded-md border bg-gray-50 px-3 py-2">
          <code className="block text-sm text-gray-800 font-mono whitespace-pre-wrap break-words">{formula}</code>
        </div>
      </div>
    </div>
  );
}
